import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { isValidCPF } from '../utils/validators';
import { fetchAddressByCEP as fetchAddressByCEPUtil } from '../utils/cep';

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 8px;

  .required {
    color: ${props => props.theme.colors.error};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.$error ? props.theme.colors.error : '#e2e8f0'};
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  background-color: ${props => props.theme.colors.white};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  margin-top: 5px;
  display: ${props => props.show ? 'block' : 'none'};
`;

const InfoBox = styled.div`
  background: #f0f9ff;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #3b82f6;
  font-size: 14px;
  color: #1e40af;
  margin-bottom: 20px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;

  input[type="checkbox"] {
    transform: scale(1.2);
    margin: 0;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    color: ${props => props.theme.colors.grayDark};
    font-weight: 500;
    cursor: pointer;
    margin: 0;
  }
`;

const PhoneContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ESTADOS = [
  { value: '', label: 'Selecione o estado' },
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

const FinancialResponsibleForm = ({ onDataChange, data }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    cpf: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: 'RJ',
    addressReference: '',
    phoneResidential: '',
    phone: '',
    email: '',
    isWhatsApp: false,
    ...data
  });

  const [semNumero, setSemNumero] = useState(formData.number === 'S/N');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const address = `${formData.street || ''}, ${formData.number || ''}${formData.complement ? `, ${formData.complement}` : ''}, ${formData.neighborhood || ''}, ${formData.city || ''} - ${formData.state || ''}, CEP: ${formData.cep || ''}`.trim();
    onDataChange({ ...formData, address });
  }, [formData, onDataChange]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  const applyMask = (field, value) => {
    switch (field) {
      case 'cpf':
        value = value.replace(/\D/g, '');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d)/, '$1.$2');
        value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        return value;
      case 'cep':
        value = value.replace(/\D/g, '');
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
        return value;
      case 'phone':
      case 'phoneResidential':
        value = value.replace(/\D/g, '').substring(0, 11);
        if (value.length <= 10) {
          value = value.replace(/(\d{2})(\d)/, '($1) $2');
          value = value.replace(/(\d{4})(\d)/, '$1-$2');
        } else {
          value = value.replace(/(\d{2})(\d)/, '($1) $2');
          value = value.replace(/(\d{5})(\d)/, '$1-$2');
        }
        return value;
      default:
        return value;
    }
  };

  const handleMaskedInputChange = (field, value) => {
    handleInputChange(field, applyMask(field, value));
  };

  const handleSemNumeroChange = (checked) => {
    setSemNumero(checked);
    handleInputChange('number', checked ? 'S/N' : '');
    if (checked) {
      setFieldErrors(prev => ({ ...prev, number: false }));
    }
  };

  const fetchAddressByCEP = async (cep) => {
    const address = await fetchAddressByCEPUtil(cep);
    if (address) {
      setFormData(prev => ({ ...prev, ...address }));
    }
  };

  const handleBlur = (field) => {
    let isValid = true;
    const value = formData[field];

    if (field === 'cpf') {
      isValid = isValidCPF(value);
    } else if (field === 'cep') {
      isValid = /^\d{5}-\d{3}$/.test(value);
      if (isValid) fetchAddressByCEP(value);
    } else if (field === 'email') {
      isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    } else if (field === 'phone') {
      isValid = /^\(\d{2}\) \d{4,5}-\d{4}$/.test(value);
    } else if (['fullName', 'street', 'number', 'neighborhood', 'city', 'state'].includes(field)) {
      isValid = value && value.trim().length > 0;
    }

    setFieldErrors(prev => ({ ...prev, [field]: !isValid }));
  };

  return (
    <>
      <SectionTitle>Responsável Administrativo e Financeiro</SectionTitle>

      <InfoBox>
        Você será o responsável pelo plano, incluindo as <b>responsabilidades administrativas e financeiras</b>. "Os dados do titular (beneficiário) deverão ser informados abaixo" (quem possui a cobertura do plano).
      </InfoBox>

      <FormGroup>
        <Label htmlFor="fin-fullName">Nome Completo <span className="required">*</span></Label>
        <Input
          id="fin-fullName"
          type="text"
          value={formData.fullName}
          onChange={(e) => handleInputChange('fullName', e.target.value)}
          onBlur={() => handleBlur('fullName')}
          $error={fieldErrors.fullName}
          placeholder="Nome do responsável financeiro"
        />
        <ErrorMessage show={fieldErrors.fullName}>Informe o nome completo</ErrorMessage>
      </FormGroup>

      <FormRow>
        <FormGroup>
          <Label htmlFor="fin-cpf">CPF <span className="required">*</span></Label>
          <Input
            id="fin-cpf"
            type="text"
            value={formData.cpf}
            onChange={(e) => handleMaskedInputChange('cpf', e.target.value)}
            onBlur={() => handleBlur('cpf')}
            $error={fieldErrors.cpf}
            maxLength="14"
            placeholder="000.000.000-00"
          />
          <ErrorMessage show={fieldErrors.cpf}>Informe um CPF válido</ErrorMessage>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="fin-email">E-mail <span className="required">*</span></Label>
          <Input
            id="fin-email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            $error={fieldErrors.email}
            placeholder="email@exemplo.com"
          />
          <ErrorMessage show={fieldErrors.email}>Informe um e-mail válido</ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="fin-phone">Telefone Celular <span className="required">*</span></Label>
          <PhoneContainer>
            <Input
              id="fin-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleMaskedInputChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              $error={fieldErrors.phone}
              maxLength={15}
              placeholder="(21) 99999-9999"
            />
            <CheckboxContainer>
              <input
                type="checkbox"
                id="fin-isWhatsApp"
                checked={!!formData.isWhatsApp}
                onChange={(e) => handleInputChange('isWhatsApp', e.target.checked)}
              />
              <label htmlFor="fin-isWhatsApp">
                Este é um número de WhatsApp
              </label>
            </CheckboxContainer>
          </PhoneContainer>
          <ErrorMessage show={fieldErrors.phone}>Informe um telefone celular válido</ErrorMessage>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="fin-phoneResidential">Telefone Residencial</Label>
          <Input
            id="fin-phoneResidential"
            type="tel"
            value={formData.phoneResidential}
            onChange={(e) => handleMaskedInputChange('phoneResidential', e.target.value)}
            maxLength={15}
            placeholder="(21) 3333-3333"
          />
        </FormGroup>
      </FormRow>

      <FormGroup>
        <Label htmlFor="fin-cep">CEP <span className="required">*</span></Label>
        <Input
          id="fin-cep"
          type="text"
          value={formData.cep}
          onChange={(e) => handleMaskedInputChange('cep', e.target.value)}
          onBlur={() => handleBlur('cep')}
          $error={fieldErrors.cep}
          maxLength="9"
          placeholder="00000-000"
        />
        <ErrorMessage show={fieldErrors.cep}>Informe um CEP válido</ErrorMessage>
      </FormGroup>

      <FormRow>
        <FormGroup>
          <Label htmlFor="fin-street">Logradouro <span className="required">*</span></Label>
          <Input
            id="fin-street"
            type="text"
            value={formData.street}
            onChange={(e) => handleInputChange('street', e.target.value)}
            onBlur={() => handleBlur('street')}
            $error={fieldErrors.street}
          />
          <ErrorMessage show={fieldErrors.street}>Informe o logradouro</ErrorMessage>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="fin-number">Número <span className="required">*</span></Label>
          <Input
            id="fin-number"
            type="text"
            value={formData.number}
            onChange={(e) => handleInputChange('number', e.target.value)}
            onBlur={() => handleBlur('number')}
            $error={fieldErrors.number}
            disabled={semNumero}
          />
          <CheckboxContainer>
            <input
              type="checkbox"
              id="fin-semNumero"
              checked={semNumero}
              onChange={(e) => handleSemNumeroChange(e.target.checked)}
            />
            <label htmlFor="fin-semNumero">Sem número</label>
          </CheckboxContainer>
          <ErrorMessage show={fieldErrors.number}>Informe o número</ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="fin-complement">Complemento</Label>
          <Input
            id="fin-complement"
            type="text"
            value={formData.complement}
            onChange={(e) => handleInputChange('complement', e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="fin-neighborhood">Bairro <span className="required">*</span></Label>
          <Input
            id="fin-neighborhood"
            type="text"
            value={formData.neighborhood}
            onChange={(e) => handleInputChange('neighborhood', e.target.value)}
            onBlur={() => handleBlur('neighborhood')}
            $error={fieldErrors.neighborhood}
          />
          <ErrorMessage show={fieldErrors.neighborhood}>Informe o bairro</ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="fin-city">Cidade <span className="required">*</span></Label>
          <Input
            id="fin-city"
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            onBlur={() => handleBlur('city')}
            $error={fieldErrors.city}
          />
          <ErrorMessage show={fieldErrors.city}>Informe a cidade</ErrorMessage>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="fin-state">Estado <span className="required">*</span></Label>
          <Select
            id="fin-state"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            onBlur={() => handleBlur('state')}
            $error={fieldErrors.state}
          >
            {ESTADOS.map(estado => (
              <option key={estado.value} value={estado.value}>{estado.label}</option>
            ))}
          </Select>
          <ErrorMessage show={fieldErrors.state}>Selecione o estado</ErrorMessage>
        </FormGroup>
      </FormRow>

      <FormGroup>
        <Label htmlFor="fin-addressReference">Referência do Endereço</Label>
        <Input
          id="fin-addressReference"
          type="text"
          value={formData.addressReference}
          onChange={(e) => handleInputChange('addressReference', e.target.value)}
          placeholder="Ex.: Próximo ao mercado, casa azul..."
        />
      </FormGroup>
    </>
  );
};

export default FinancialResponsibleForm;
